import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  Icon,
  Chip,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import {
  RFQ,
  AddVendorQuotationRequest,
  VendorQuotationItem,
} from "../../types/rfq.types";
import { rfqService } from "../../services/rfq.service";

interface EnhancedVendorQuotationImportProps {
  open: boolean;
  onClose: () => void;
  rfq: RFQ;
  onImportComplete: () => void;
}

interface ParsedQuotationData {
  quotation_details: {
    quotation_number?: string;
    quotation_date?: Date;
    validity_date: Date;
    currency: string;
    payment_terms?: string;
    delivery_terms?: string;
    estimated_delivery_days: number;
    notes?: string;
  };
  items: Array<{
    item_name: string;
    requested_quantity: number;
    uom: string;
    quoted_price_per_unit: number;
    total_price: number;
    supplier_item_code?: string;
    notes?: string;
  }>;
  validation_errors: string[];
  validation_warnings: string[];
}

const steps = [
  "Upload File",
  "Select Supplier",
  "Validate Data",
  "Preview & Import",
];

export const EnhancedVendorQuotationImport: React.FC<
  EnhancedVendorQuotationImportProps
> = ({ open, onClose, rfq, onImportComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedQuotationData[]>([]);
  const [importing, setImporting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [existingQuotations, setExistingQuotations] = useState<string[]>([]);
  const [loadingQuotations, setLoadingQuotations] = useState(false);

  // Load existing quotations for this RFQ
  const loadExistingQuotations = useCallback(async () => {
    setLoadingQuotations(true);
    try {
      const response = await rfqService.getRFQQuotations({ _id: rfq._id });
      if (response.success) {
        const supplierIds = response.data.map(
          (quotation) => quotation.supplier_id
        );
        setExistingQuotations(supplierIds);
      }
    } catch (error) {
      console.error("Error loading existing quotations:", error);
      // Don't show error to user, just log it
    } finally {
      setLoadingQuotations(false);
    }
  }, [rfq._id]);

  // Load existing quotations when dialog opens
  React.useEffect(() => {
    if (open) {
      loadExistingQuotations();
    }
  }, [open, loadExistingQuotations]);

  // Generate enhanced Excel template
  const generateExcelTemplate = useCallback(() => {
    const workbook = XLSX.utils.book_new();

    // Sheet 1: Quotation Details Template (Supplier info removed - will be selected after upload)
    const quotationData = [
      ["Field", "Value", "Description", "Required"],
      ["RFQ Number", rfq.rfq_number, "Auto-filled from RFQ", "Yes"],
      ["Quotation Number", "", "Your internal quotation reference", "No"],
      [
        "Quotation Date",
        new Date().toISOString().split("T")[0],
        "Date of quotation (YYYY-MM-DD)",
        "No",
      ],
      [
        "Validity Date",
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        "Quote valid until (YYYY-MM-DD)",
        "Yes",
      ],
      ["Currency", "USD", "USD, EUR, GBP, etc.", "Yes"],
      ["Payment Terms", "", "e.g., Net 30, Advance Payment", "No"],
      ["Delivery Terms", "", "e.g., FOB, CIF, DDP", "No"],
      ["Estimated Delivery Days", "", "Number of days for delivery", "Yes"],
      ["Notes", "", "Additional comments", "No"],
    ];
    const quotationSheet = XLSX.utils.aoa_to_sheet(quotationData);
    XLSX.utils.book_append_sheet(workbook, quotationSheet, "Quotation Details");

    // Sheet 2: Item Pricing Template
    const itemHeaders = [
      "Item Name",
      "Requested Qty",
      "UOM",
      "Quoted Price Per Unit",
      "Total Price",
      "Supplier Item Code",
      "Notes",
    ];

    const itemData = [itemHeaders];

    // Pre-populate with RFQ items and correct formulas
    rfq.pr_items?.forEach((item, index) => {
      const rowNumber = index + 2; // Row 1 is headers, so data starts at row 2
      itemData.push([
        item.item_name,
        item.quantity.toString(), // Convert to string for Excel
        item.uom,
        "0", // User to fill - default to 0 as string
        `=B${rowNumber}*D${rowNumber}`, // Formula as string, not object
        "", // Supplier item code
        item.reason || "", // Notes
      ]);
    });

    const itemSheet = XLSX.utils.aoa_to_sheet(itemData);
    XLSX.utils.book_append_sheet(workbook, itemSheet, "Item Pricing");

    // Sheet 3: Instructions (Terms & Conditions sheet removed)
    const instructionsData = [
      ["Enhanced Quotation Import Instructions"],
      [""],
      [
        '1. Fill in the "Quotation Details" sheet with your quotation information',
      ],
      ['2. Complete the "Item Pricing" sheet with your quoted prices'],
      ["3. Save the file and upload it back to the system"],
      ["4. Select the supplier during the import process"],
      [""],
      ["Important Notes:"],
      ["• You will select the supplier after uploading the file"],
      ["• Dates should be in YYYY-MM-DD format"],
      ["• Prices should be numeric values only"],
      ["• Total prices will be auto-calculated based on Quantity × Unit Price"],
      [""],
      ["Available Suppliers for this RFQ:"],
      ...rfq.supplier_details.map((s) => ["• " + (s.supplier_name || s.name)]),
    ];
    const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsData);
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");

    // Generate and download
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rfq-${rfq.rfq_number}-enhanced-quotation-template.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  }, [rfq]);

  // File drop zone configuration
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setFile(file);
      parseFile(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    maxFiles: 1,
  });

  // Enhanced file parsing with multi-sheet support
  const parseFile = async (file: File) => {
    setParsing(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });

      const quotations: ParsedQuotationData[] = [];

      // Parse different file types
      if (file.name.endsWith(".csv")) {
        // Handle CSV format (simplified)
        const quotation = parseCSVFormat(workbook);
        if (quotation) quotations.push(quotation);
      } else {
        // Handle Excel format (multi-sheet)
        const quotation = parseExcelFormat(workbook);
        if (quotation) quotations.push(quotation);
      }

      setParsedData(quotations);

      if (quotations.length > 0) {
        toast.success(`File parsed successfully. Please select a supplier.`);
        setActiveStep(1); // Move to supplier selection step
      } else {
        toast.error("No valid quotations found in file");
      }
    } catch (error) {
      console.error("Error parsing file:", error);
      toast.error("Failed to parse file. Please check the format.");
    } finally {
      setParsing(false);
    }
  };

  // Parse Excel format with multiple sheets
  const parseExcelFormat = (
    workbook: XLSX.WorkBook
  ): ParsedQuotationData | null => {
    const quotationSheet = workbook.Sheets["Quotation Details"];
    const itemSheet = workbook.Sheets["Item Pricing"];

    if (!quotationSheet || !itemSheet) {
      throw new Error(
        "Required sheets not found. Please use the provided template."
      );
    }

    const quotationData = XLSX.utils.sheet_to_json(quotationSheet, {
      header: 1,
    });
    const itemData = XLSX.utils.sheet_to_json(itemSheet, { header: 1 });

    // Parse quotation details
    const quotationDetails: any = {};
    quotationData.slice(1).forEach((row: any) => {
      const [field, value] = row;
      if (field && value !== undefined) {
        quotationDetails[field.toLowerCase().replace(/ /g, "_")] = value;
      }
    });

    // Parse items
    const items: any[] = [];
    const itemHeaders = itemData[0] as string[];
    itemData.slice(1).forEach((row: any) => {
      if (row.length > 0 && row[0]) {
        // Skip empty rows
        const item: any = {};
        itemHeaders.forEach((header, index) => {
          if (header) {
            item[header.toLowerCase().replace(/ /g, "_")] = row[index];
          }
        });
        items.push(item);
      }
    });

    // Validation
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate required fields
    if (!quotationDetails.validity_date) {
      errors.push("Validity date is required");
    } else {
      const validityDate = new Date(quotationDetails.validity_date);
      if (validityDate <= new Date()) {
        errors.push("Validity date must be in the future");
      }
    }

    if (
      !quotationDetails.estimated_delivery_days ||
      quotationDetails.estimated_delivery_days < 1
    ) {
      errors.push("Valid estimated delivery days required");
    }

    // Validate items
    if (items.length === 0) {
      errors.push("At least one item must be quoted");
    }

    items.forEach((item, index) => {
      if (
        !item.quoted_price_per_unit ||
        isNaN(Number(item.quoted_price_per_unit))
      ) {
        errors.push(`Item ${index + 1}: Valid quoted price required`);
      }
      if (Number(item.quoted_price_per_unit) < 0) {
        errors.push(`Item ${index + 1}: Price cannot be negative`);
      }
    });

    return {
      quotation_details: {
        quotation_number: quotationDetails.quotation_number,
        quotation_date: quotationDetails.quotation_date
          ? new Date(quotationDetails.quotation_date)
          : new Date(),
        validity_date: new Date(quotationDetails.validity_date),
        currency: quotationDetails.currency || "USD",
        payment_terms: quotationDetails.payment_terms,
        delivery_terms: quotationDetails.delivery_terms,
        estimated_delivery_days:
          Number(quotationDetails.estimated_delivery_days) || 0,
        notes: quotationDetails.notes,
      },
      items: items.map((item) => ({
        item_name: item.item_name,
        requested_quantity: Number(item.requested_qty) || 0,
        uom: item.uom,
        quoted_price_per_unit: Number(item.quoted_price_per_unit) || 0,
        total_price:
          Number(item.total_price) ||
          Number(item.requested_qty) * Number(item.quoted_price_per_unit),
        supplier_item_code: item.supplier_item_code,
        notes: item.notes,
      })),
      validation_errors: errors,
      validation_warnings: warnings,
    };
  };

  // Parse CSV format (simplified)
  const parseCSVFormat = (
    workbook: XLSX.WorkBook
  ): ParsedQuotationData | null => {
    // Implementation for CSV parsing (similar to existing functionality)
    // This would be the fallback for users who prefer CSV
    return null; // Placeholder
  };

  // Import quotations
  const handleImport = async () => {
    if (!selectedSupplier) {
      toast.error("Please select a supplier first");
      return;
    }

    // Extract supplier ID with multiple fallbacks
    const supplierId =
      selectedSupplier.supplier_id ||
      selectedSupplier.id ||
      selectedSupplier._id ||
      selectedSupplier.value;

    if (!supplierId) {
      toast.error(
        "Invalid supplier selection. Please select a supplier again."
      );
      console.error(
        "Supplier ID not found in selected supplier:",
        selectedSupplier
      );
      return;
    }

    const validQuotations = parsedData.filter(
      (q) => q.validation_errors.length === 0
    );
    if (validQuotations.length === 0) {
      toast.error("No valid quotations to import");
      return;
    }

    setImporting(true);
    setImportProgress(0);

    try {
      for (let i = 0; i < validQuotations.length; i++) {
        const quotation = validQuotations[i];

        // Prepare items for API
        const quotationItems: VendorQuotationItem[] = quotation.items.map(
          (item) => ({
            item_name: item.item_name,
            quantity: item.requested_quantity,
            uom: item.uom,
            price_per_unit: item.quoted_price_per_unit,
            total_price: item.total_price,
            urgency: "medium", // Default value
            reason: item.notes,
          })
        );

        const request: AddVendorQuotationRequest = {
          rfq_id: rfq._id,
          supplier_id: supplierId,
          quoted_price: quotation.items.reduce(
            (sum, item) => sum + item.total_price,
            0
          ),
          quoted_price_override: false,
          estimated_delivery_days:
            quotation.quotation_details.estimated_delivery_days,
          quote_validity_date:
            quotation.quotation_details.validity_date.getTime(),
          currency: quotation.quotation_details.currency,
          payment_terms: quotation.quotation_details.payment_terms,
          delivery_terms: quotation.quotation_details.delivery_terms,
          notes: quotation.quotation_details.notes,
          items: quotationItems,
        };

        await rfqService.addVendorQuotation(request);
        setImportProgress(((i + 1) / validQuotations.length) * 100);
      }

      toast.success(
        `Successfully imported ${validQuotations.length} quotation(s)`
      );
      onImportComplete();
      handleClose();
    } catch (error: any) {
      console.error("Import error:", error);
      console.error("Error details:", error.response?.data || error.message);
      toast.error(
        `Failed to import quotations: ${
          error.response?.data?.error?.message || error.message
        }`
      );
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedData([]);
    setActiveStep(0);
    setImportProgress(0);
    setSelectedSupplier(null);
    setExistingQuotations([]);
    onClose();
  };

  const validCount = parsedData.filter(
    (q) => q.validation_errors.length === 0
  ).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              bgcolor: "primary.main",
              borderRadius: "12px",
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ color: "white", fontSize: 24 }}>cloud_upload</Icon>
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Enhanced Quotation Import
            </Typography>
            <Typography variant="body2" color="textSecondary">
              RFQ: {rfq.rfq_number} • Import quotations with item-level pricing
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {/* Progress Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          {activeStep === 0 && (
            <Box>
              {/* Instructions and Template Download */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <AlertTitle>Enhanced Import Features</AlertTitle>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  ✅ <strong>Item-level pricing</strong> with auto-calculated
                  totals
                  <br />✅ <strong>Pre-populated templates</strong> with RFQ
                  data
                  <br />✅ <strong>Supplier auto-mapping</strong> from RFQ
                  suppliers
                  <br />✅ <strong>Comprehensive validation</strong> with
                  detailed error reports
                  <br />✅ <strong>Multiple file format support</strong> (Excel,
                  CSV)
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        📊 Excel Template (Recommended)
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mb: 2 }}
                      >
                        Multi-sheet template with pre-populated RFQ data and
                        item-level pricing
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Icon>download</Icon>}
                        onClick={generateExcelTemplate}
                        fullWidth
                      >
                        Download Excel Template
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        📄 CSV Template (Simple)
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mb: 2 }}
                      >
                        Simple CSV format for basic quotation data
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Icon>download</Icon>}
                        fullWidth
                        disabled
                      >
                        Coming Soon
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* File Upload Area */}
              <Paper
                {...getRootProps()}
                sx={{
                  mt: 3,
                  p: 4,
                  border: "2px dashed",
                  borderColor: isDragActive ? "primary.main" : "grey.300",
                  bgcolor: isDragActive ? "primary.50" : "grey.50",
                  cursor: "pointer",
                  textAlign: "center",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "primary.50",
                  },
                }}
              >
                <input {...getInputProps()} />
                <Icon sx={{ fontSize: 48, color: "primary.main", mb: 2 }}>
                  cloud_upload
                </Icon>
                <Typography variant="h6" gutterBottom>
                  {isDragActive
                    ? "Drop file here"
                    : "Drag & drop or click to upload"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Supports Excel (.xlsx, .xls) and CSV files
                </Typography>
                {file && (
                  <Chip
                    label={file.name}
                    color="primary"
                    sx={{ mt: 2 }}
                    onDelete={() => setFile(null)}
                  />
                )}
              </Paper>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select Supplier
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Choose the supplier for this quotation from the available
                suppliers for this RFQ.
              </Typography>

              {loadingQuotations && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Checking existing quotations...
                  </Typography>
                </Alert>
              )}

              {!loadingQuotations && existingQuotations.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>
                    Some suppliers already have quotations
                  </AlertTitle>
                  <Typography variant="body2">
                    Suppliers with existing quotations are marked and cannot be
                    selected. You can only submit one quotation per supplier per
                    RFQ.
                  </Typography>
                </Alert>
              )}

              <Grid container spacing={2}>
                {rfq.supplier_details.map((supplier) => {
                  const hasExistingQuotation = existingQuotations.includes(
                    supplier.supplier_id || supplier._id || ""
                  );
                  const isSelected =
                    selectedSupplier?.supplier_id === supplier.supplier_id ||
                    selectedSupplier?._id === supplier._id;
                  const isDisabled = hasExistingQuotation;

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={supplier.supplier_id || supplier._id}
                    >
                      <Card
                        sx={{
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          border: "2px solid",
                          borderColor: isSelected
                            ? "primary.main"
                            : isDisabled
                            ? "warning.main"
                            : "grey.200",
                          bgcolor: isSelected
                            ? "primary.50"
                            : isDisabled
                            ? "warning.50"
                            : "white",
                          opacity: isDisabled ? 0.7 : 1,
                          "&:hover": !isDisabled
                            ? {
                                borderColor: "primary.main",
                                bgcolor: "primary.50",
                              }
                            : {},
                        }}
                        onClick={() => {
                          if (isDisabled) {
                            toast.warning(
                              `${
                                supplier.supplier_name || supplier.name
                              } already has a quotation for this RFQ.`
                            );
                            return;
                          }
                          setSelectedSupplier(supplier);
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            {isSelected ? (
                              <Icon color="primary">check_circle</Icon>
                            ) : hasExistingQuotation ? (
                              <Icon color="warning">warning</Icon>
                            ) : (
                              <Icon color="disabled">
                                radio_button_unchecked
                              </Icon>
                            )}
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography variant="subtitle1">
                                  {supplier.supplier_name || supplier.name}
                                </Typography>
                                {hasExistingQuotation && (
                                  <Chip
                                    label="Quotation Exists"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              {supplier.contact_email && (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {supplier.contact_email}
                                </Typography>
                              )}
                              {hasExistingQuotation && (
                                <Typography
                                  variant="caption"
                                  color="warning.main"
                                  sx={{ mt: 0.5, display: "block" }}
                                >
                                  This supplier already submitted a quotation
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {selectedSupplier && (
                <Box
                  sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="contained"
                    onClick={() => {
                      // Run validation now with selected supplier
                      if (parsedData.length > 0) {
                        const validQuotations = parsedData.filter(
                          (q) => q.validation_errors.length === 0
                        );
                        toast.success(
                          `Supplier selected. Found ${parsedData.length} quotation(s), ${validQuotations.length} valid`
                        );
                        setActiveStep(3);
                      }
                    }}
                  >
                    Continue to Validation
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Validating Data...
              </Typography>
              {parsing && <LinearProgress sx={{ mb: 3 }} />}
            </Box>
          )}

          {activeStep === 3 && parsedData.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography variant="h6">
                  Preview & Validation Results
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Chip
                    label={`${validCount} Valid`}
                    color="success"
                    size="small"
                  />
                  <Chip
                    label={`${parsedData.length - validCount} Invalid`}
                    color="error"
                    size="small"
                  />
                </Box>
              </Box>

              {parsedData.map((quotation, index) => (
                <Accordion key={index} defaultExpanded>
                  <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        width: "100%",
                      }}
                    >
                      <Chip
                        label={
                          quotation.validation_errors.length === 0
                            ? "Valid"
                            : "Invalid"
                        }
                        color={
                          quotation.validation_errors.length === 0
                            ? "success"
                            : "error"
                        }
                        size="small"
                      />
                      <Typography variant="subtitle1">
                        {selectedSupplier?.supplier_name ||
                          selectedSupplier?.name ||
                          "Selected Supplier"}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ ml: "auto" }}
                      >
                        {quotation.items.length} items • Total:{" "}
                        {quotation.quotation_details.currency}{" "}
                        {quotation.items
                          .reduce((sum, item) => sum + item.total_price, 0)
                          .toFixed(2)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {quotation.validation_errors.length > 0 && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        <AlertTitle>Validation Errors</AlertTitle>
                        <List dense>
                          {quotation.validation_errors.map((error, i) => (
                            <ListItem key={i}>
                              <ListItemIcon>
                                <Icon color="error">error</Icon>
                              </ListItemIcon>
                              <ListItemText primary={error} />
                            </ListItem>
                          ))}
                        </List>
                      </Alert>
                    )}

                    {quotation.validation_warnings.length > 0 && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <AlertTitle>Warnings</AlertTitle>
                        <List dense>
                          {quotation.validation_warnings.map((warning, i) => (
                            <ListItem key={i}>
                              <ListItemIcon>
                                <Icon color="warning">warning</Icon>
                              </ListItemIcon>
                              <ListItemText primary={warning} />
                            </ListItem>
                          ))}
                        </List>
                      </Alert>
                    )}

                    {/* Quotation Details */}
                    <Typography variant="subtitle2" gutterBottom>
                      Quotation Details
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption">
                          Selected Supplier
                        </Typography>
                        <Typography variant="body2">
                          {selectedSupplier?.supplier_name ||
                            selectedSupplier?.name ||
                            "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption">Validity Date</Typography>
                        <Typography variant="body2">
                          {quotation.quotation_details.validity_date.toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption">Delivery Days</Typography>
                        <Typography variant="body2">
                          {quotation.quotation_details.estimated_delivery_days}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption">Currency</Typography>
                        <Typography variant="body2">
                          {quotation.quotation_details.currency}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Items Table */}
                    <Typography variant="subtitle2" gutterBottom>
                      Item Pricing ({quotation.items.length} items)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Item Name</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell>UOM</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {quotation.items.map((item, i) => (
                            <TableRow key={i}>
                              <TableCell>{item.item_name}</TableCell>
                              <TableCell align="right">
                                {item.requested_quantity}
                              </TableCell>
                              <TableCell>{item.uom}</TableCell>
                              <TableCell align="right">
                                {quotation.quotation_details.currency}{" "}
                                {item.quoted_price_per_unit.toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                {quotation.quotation_details.currency}{" "}
                                {item.total_price.toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}

              {importing && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    Importing quotations... {importProgress.toFixed(0)}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={importProgress}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={importing}>
          Cancel
        </Button>

        {activeStep === 0 && (
          <Button
            variant="contained"
            startIcon={<Icon>download</Icon>}
            onClick={generateExcelTemplate}
          >
            Download Template
          </Button>
        )}

        {activeStep === 3 && (
          <LoadingButton
            onClick={handleImport}
            loading={importing}
            variant="contained"
            disabled={validCount === 0 || !selectedSupplier}
            startIcon={<Icon>upload</Icon>}
          >
            Import {validCount} Quotation{validCount !== 1 ? "s" : ""}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
